// Shade pass + debug-colour write + NeuralBSDF runner, split from HairRenderer for testability.
// Helpers mutate the caller's HairData in place; self (HairRenderer) supplies non-data state.

import * as THREE from 'three';
import { shadeGaussiansEnvmap } from '../shading.js';
import { setPackedSplatRgb } from './splatUtil.js';
import { camPosInSplatLocal } from './geom.js';
import { snapshotProbe } from './envInstall.js';
import { HairGpuShader } from './hairGpuShader.js';

// Module-level scratch for the shade pass — single HairRenderer per page.
const _shadeTmpVec = new THREE.Vector3();

// One-shot "missing data" popup on spec_mod error (once per page, no per-frame spam).
let _missingDataShown = false;
function showHairMissingDataPopup(detail) {
  if (_missingDataShown || typeof document === 'undefined') return;
  _missingDataShown = true;
  const el = document.createElement('div');
  el.setAttribute('data-hair-missing-data', '');
  el.style.cssText = [
    'position:fixed', 'top:16px', 'right:16px', 'z-index:99999',
    'background:#222', 'color:#fff', 'padding:12px 16px',
    'border:1px solid #f55', 'border-radius:6px',
    'font-family:var(--m-font)', 'font-size:13px',
    'box-shadow:0 4px 12px rgba(0,0,0,0.4)', 'max-width:320px',
  ].join(';');
  el.innerHTML = `
    <div style="font-weight:600;margin-bottom:4px;color:#f99">Hair shading: missing data</div>
    <div style="opacity:0.85;margin-bottom:8px">spec_mod could not run. Hair will keep last-known shading.</div>
    <div style="font-family:var(--m-mono);font-size:11px;opacity:0.6;margin-bottom:8px;word-break:break-word">${detail}</div>
    <button style="background:#444;color:#fff;border:0;padding:4px 10px;border-radius:4px;cursor:pointer">Dismiss</button>
  `;
  el.querySelector('button').addEventListener('click', () => el.remove());
  document.body.appendChild(el);
}

// Fold spec_mod sub-timings + shade/pack/total into perfAgg, mirror to window.__HAIR_PERF__, log periodically.
function recordHairPerf(self, data, shadeMs, packMs, totalMs) {
  const sub = data._perfSample || {};
  const sample = {
    viewEnc: sub.viewEnc, specMod: sub.specMod, unpack: sub.unpack,
    shade: shadeMs, pack: packMs, total: totalMs,
  };
  data._perfSample = null;
  const agg = self.perfAgg;
  if (!agg) return;
  const frames = agg.record(sample);
  const ep = self.runner?.activeEp ?? '?';
  if (typeof window !== 'undefined') {
    window.__HAIR_PERF__ = { P: data.P, specModEp: ep, frames, last: sample, stats: agg.snapshot() };
  }
  // Log the first few shades, then every 30.
  if (frames <= 5 || frames % 30 === 0) {
    const f = (x) => (typeof x === 'number' ? x.toFixed(1) : '?');
    const s = sample;
    const blocked = self.runner?.specEpError ? ` webgpu_blocked_by="${self.runner.specEpError}"` : '';
    console.info(`[hair][perf] #${frames} spec_mod_EP=${ep} viewEnc=${f(s.viewEnc)} specMod=${f(s.specMod)} unpack=${f(s.unpack)} shade=${f(s.shade)} pack=${f(s.pack)} total=${f(s.total)} ms${blocked}`);
  }
}

// Run NeuralBSDF once → data.neuralBsdfReflectance (1ch broadcast to 3ch); on failure fill 1.0 (identity).
// Returns true when the model actually ran — callers use this to decide whether
// to record the SH as "computed", so a transient failure is retried next shade.
export async function runNeuralBsdfOnce(runner, shCoeff48, data) {
  const { fDec, P, neuralBsdfReflectance } = data;
  const tBsdf = performance.now();
  try {
    const res = await runner.runNeuralBsdf(shCoeff48, fDec);
    if (res.gpuBuffer) {
      // gpuShade path: reflectance stays on the GPU; the WGSL shade binds it
      // directly (1ch broadcast happens in the shader). No CPU copy exists, so
      // the stats log + neuralBsdfReflectance stay stale — 'bsdf' debug mode is
      // already documented as stale under gpushade.
      console.info(`[hair] NeuralBSDF ran in ${(performance.now() - tBsdf).toFixed(0)}ms; outCh=${res.outCh} (gpu-resident)`);
      data._reflGpuBuffer = res.gpuBuffer;
      data._reflCh = res.outCh;
      data._reflVersion = (data._reflVersion ?? 0) + 1;
      return true;
    }
    data._reflGpuBuffer = null;
    const { reflectance, outCh } = res;
    console.info(`[hair] NeuralBSDF ran in ${(performance.now() - tBsdf).toFixed(0)}ms; outCh=${outCh}`);
    if (outCh === 3) {
      neuralBsdfReflectance.set(reflectance);
    } else if (outCh === 1) {
      for (let i = 0; i < P; i++) {
        const v = reflectance[i];
        neuralBsdfReflectance[i * 3]     = v;
        neuralBsdfReflectance[i * 3 + 1] = v;
        neuralBsdfReflectance[i * 3 + 2] = v;
      }
    } else {
      console.warn(`[hair] NeuralBSDF out_ch=${outCh} not 1 or 3; using identity reflectance`);
      neuralBsdfReflectance.fill(1);
    }
    const stats = [0, 1, 2].map((c) => {
      let lo = Infinity, hi = -Infinity, sum = 0;
      for (let i = 0; i < P; i++) {
        const v = neuralBsdfReflectance[i * 3 + c];
        if (v < lo) lo = v; if (v > hi) hi = v; sum += v;
      }
      return { lo, hi, mean: sum / P };
    });
    console.info('[hair] NeuralBSDF reflectance per-channel:',
      'R=', stats[0], 'G=', stats[1], 'B=', stats[2]);
  } catch (e) {
    console.warn('[hair] NeuralBSDF failed; using identity reflectance:', e);
    data._reflGpuBuffer = null; // fall back to the CPU 3ch path for the shade bind
    neuralBsdfReflectance.fill(1);
    data._reflVersion = (data._reflVersion ?? 0) + 1;
    return false;
  }
  // Bump so the GPU shader (gpushade) knows to re-upload reflectance this frame.
  data._reflVersion = (data._reflVersion ?? 0) + 1;
  return true;
}

// Write packed splat colors per debugMode; all encode floats into uint8 RGB [0,1].
// Colors are packed LINEAR (setPackedSplatRgb clamps + rounds to u8); Spark's
// splat shader applies no output transform, matching the WGSL shade's pack.
export function writeDebugColors(splatMesh, mode, data) {
  const { outColors, outDiffuse, outSpecular, albedo, neuralBsdfReflectance, roughness, normals, P } = data;
  const ps = splatMesh.packedSplats;
  const pa = ps.packedArray;
  if (mode === 'roughness') {
    for (let i = 0; i < P; i++) {
      const r = roughness[i];
      setPackedSplatRgb(pa, i, r, r, r);
    }
  } else if (mode === 'normal') {
    for (let i = 0; i < P; i++) {
      // Current normal mapped [-1,1]→[0,1] like a standard normal-map viz.
      setPackedSplatRgb(pa, i,
        normals[i * 3]     * 0.5 + 0.5,
        normals[i * 3 + 1] * 0.5 + 0.5,
        normals[i * 3 + 2] * 0.5 + 0.5);
    }
  } else if (mode === 'bsdf') {
    // Clamp reflectance at 1.0 to avoid uint8 overflow (>1 means off-distribution input).
    for (let i = 0; i < P; i++) {
      setPackedSplatRgb(pa, i,
        Math.min(1, neuralBsdfReflectance[i * 3]),
        Math.min(1, neuralBsdfReflectance[i * 3 + 1]),
        Math.min(1, neuralBsdfReflectance[i * 3 + 2]));
    }
  } else {
    // 'shaded' (final radiance) + the raw data views (diffuse/specular/albedo).
    const src = mode === 'diffuse' ? outDiffuse
      : mode === 'specular' ? outSpecular
        : mode === 'albedo' ? albedo
          : outColors;
    for (let i = 0; i < P; i++) {
      setPackedSplatRgb(pa, i, src[i * 3], src[i * 3 + 1], src[i * 3 + 2]);
    }
  }
  ps.needsUpdate = true;
}

// Single shading pass: compute per-Gaussian RGB and write into the packed array.
export async function shadeFrame(self, camera, useSpecModForThisFrame, runSpecModFn) {
  const data = self.data;
  const { P } = data;
  const tTotal = performance.now();

  // spec_mod is the only shading path; on error popup once and skip so last frame's data stays.
  try {
    await runSpecModFn(camera);
  } catch (e) {
    console.warn('[hair] spec_mod failed:', e);
    showHairMissingDataPopup(e?.message || String(e));
    return;
  }

  // Refresh the env SH from the live light probe right before shading.
  snapshotProbe(self);

  // Splat-local camera pos so per-Gaussian view dirs track the PLY frame as the head bone moves it.
  const camPos = camPosInSplatLocal(camera, self.splatMesh, _shadeTmpVec);
  const tShade = performance.now();
  if (self.runner?.gpuShade && data._specGpuBuffer) {
    // Fused WGSL shade: unpack + BecomingLit shade on-GPU, reading spec_out
    // straight from its gpu-buffer. Only the final colours read back.
    if (!self._gpuShader) self._gpuShader = new HairGpuShader();
    // Idempotent: builds the pipeline + buffers on first shade. On the GPU-unpack
    // path _init already ran initStatic (+ the WGSL unpack filled the albedo
    // lanes), so init() here just skips bStatic and builds the rest.
    self._gpuShader.init(self.runner.gpuDevice(), data, self.brdfLut);
    await self._gpuShader.shade({
      specGpuBuffer: data._specGpuBuffer,
      sh27: self._sh27,
      campos: [camPos.x, camPos.y, camPos.z],
      diffuseScale: self._diffuseScale.value,
      specScale: self._specScale.value,
      useNeuralBsdf: self.flags.useNeuralBsdf,
      N: data.N,
      // spec_out sits on the grid spec_mod ran on: the tight active grid when the
      // pack allows the crop, else the padded one. `plane` comes from specMod.js
      // (data._specPlane), which already reflects that same grid.
      nPad: self.runner?.specCropActive ? data.nSpec : data.nPad,
      plane: data._specPlane ?? (data.sPad * data.nPad),
      reflVersion: data._reflVersion ?? 0,
      // gpu-resident bsdf output (bound directly, 1ch broadcast in WGSL) or the
      // CPU 3ch array uploaded into the shader-owned buffer.
      reflGpuBuffer: data._reflGpuBuffer ?? null,
      reflCh: data._reflGpuBuffer ? (data._reflCh ?? 1) : 3,
      reflectance: data.neuralBsdfReflectance,
    });
  } else {
    shadeGaussiansEnvmap({
      pos: data.positions,
      nrm: data.normals,
      albedo: data.albedo,
      roughness: data.roughness,
      specVis: data.specVis,
      campos: [camPos.x, camPos.y, camPos.z],
      neuralBsdfReflectance: data.neuralBsdfReflectance,
      diffuseEnv: self.diffuseSampler,
      specularEnv: self.specularSampler,
      fgLut: self.fgLutSampler,
      useNeuralBsdf: self.flags.useNeuralBsdf,
      outColors: data.outColors,
      outDiffuse: data.outDiffuse,
      outSpecular: data.outSpecular,
      N: P,
    });
  }
  const shadeMs = performance.now() - tShade;

  // gpushade reads colours back asynchronously — the pack happens in the
  // update() poll once the readback lands, not here (which would re-introduce a
  // sync stall). The CPU path packs inline as before.
  const tPack = performance.now();
  if (!self.runner?.gpuShade) {
    writeDebugColors(self.splatMesh, self.flags.debugMode, data);
  }
  recordHairPerf(self, data, shadeMs, performance.now() - tPack, performance.now() - tTotal);

  // CPU path only: on gpuShade these SoA arrays stay empty (the WGSL writes
  // packed colours directly), so the stats would be all-zero noise.
  if (!self.runner?.gpuShade && (!self._loggedShade || self._loggedShadeCount < 3)) {
    const ds = [0, 1, 2].map((c) => {
      let lo = Infinity, hi = -Infinity, sum = 0;
      for (let i = 0; i < P; i++) {
        const v = data.outDiffuse[i * 3 + c];
        if (v < lo) lo = v; if (v > hi) hi = v; sum += v;
      }
      return { lo, hi, mean: sum / P };
    });
    const ss = [0, 1, 2].map((c) => {
      let lo = Infinity, hi = -Infinity, sum = 0;
      for (let i = 0; i < P; i++) {
        const v = data.outSpecular[i * 3 + c];
        if (v < lo) lo = v; if (v > hi) hi = v; sum += v;
      }
      return { lo, hi, mean: sum / P };
    });
    console.info('[hair] diffuse stats: R=', ds[0], 'G=', ds[1], 'B=', ds[2]);
    console.info('[hair] specular stats: R=', ss[0], 'G=', ss[1], 'B=', ss[2]);

    self._loggedShadeCount = (self._loggedShadeCount || 0) + 1;
    self._loggedShade = true;
    const stats = [0, 1, 2].map((c) => {
      let lo = Infinity, hi = -Infinity, sum = 0;
      for (let i = 0; i < P; i++) {
        const v = data.outColors[i * 3 + c];
        if (v < lo) lo = v; if (v > hi) hi = v; sum += v;
      }
      return { lo, hi, mean: sum / P };
    });
    console.info(`[hair] shade #${self._loggedShadeCount} (useNBSDF=${self.flags.useNeuralBsdf}, useSpec=${self.flags.useSpecMod}, env=${self.flags.envSource}) per-channel:`,
      'R=', stats[0], 'G=', stats[1], 'B=', stats[2]);
  }
}
